import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { BaseModal, Button, Checkbox, useToastActions } from '@airclou/ui'
import { cancelSubscription } from '../../../services/billing'
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'

/**
 * Cancelling a subscription.
 *
 * The copy here is deliberately literal about what happens, because we do not
 * yet delete anything on cancellation. Promising a data purge we do not perform
 * would be worse than saying less — someone might cancel expecting their data
 * gone, and it would still be sitting there.
 */

export interface CancelSubscriptionModalProps {
  /** The suite whose subscription this cancels. */
  suite: string
  /** When the current period ends, for the default option's wording. */
  periodEndsAt?: string | null
  onClose: () => void
  onCancelled: () => void
}

type Timing = 'period_end' | 'immediate'

function formatDate(iso: string | null | undefined, timezone: string): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : formatDateInTz(iso, timezone)
}

export function CancelSubscriptionModal({
  suite,
  periodEndsAt,
  onClose,
  onCancelled,
}: CancelSubscriptionModalProps) {
  const timezone = useTimezone()
  const { success: toastSuccess, error: toastError } = useToastActions()
  // Default to keeping what they paid for. Making "immediate" the default would
  // take away access they have already bought.
  const [timing, setTiming] = useState<Timing>('period_end')
  const [acknowledged, setAcknowledged] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)

  const endDate = formatDate(periodEndsAt, timezone)

  async function handleConfirm() {
    setIsCancelling(true)
    try {
      const result = await cancelSubscription(suite, timing)
      // The server's answer is the truth at the moment of the cancel; the
      // periodEndsAt prop is only the pre-confirm estimate.
      const endsAt = formatDate(result.accessEndsAt, timezone) ?? endDate
      toastSuccess(
        timing === 'immediate'
          ? // Not past tense: only Stripe's webhook ends access, so at this
            // point the customer can still use the app for a few seconds.
            'Subscription cancelled. You move to the free plan once Stripe confirms, usually within a minute.'
          : endsAt
            ? `Subscription cancelled. Your plan continues until ${endsAt}, then you move to the free plan.`
            : 'Subscription cancelled. Your plan continues until the end of the period, then you move to the free plan.'
      )
      onCancelled()
    } catch (err: any) {
      toastError(err?.message || 'Could not cancel the subscription')
      setIsCancelling(false)
    }
  }

  return (
    // Shared BaseModal, not a hand-rolled overlay: focus trap, scroll lock and
    // Escape come with it. Dismissal is switched off while the cancel is in
    // flight — closing here would hide a request that is already changing the
    // subscription in Stripe.
    <BaseModal
      isOpen
      onClose={onClose}
      closeOnBackdrop={!isCancelling}
      closeOnEscape={!isCancelling}
      disableCloseButton={isCancelling}
      size="md"
      contentClassName="px-6 py-5"
      title={
        <span className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <span className="text-lg font-semibold">Cancel subscription</span>
        </span>
      }
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isCancelling}>
            Keep subscription
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!acknowledged || isCancelling}
            className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:text-gray-950 dark:hover:bg-red-400"
          >
            {isCancelling ? 'Cancelling...' : 'Cancel subscription'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <label className="flex items-start gap-2 text-sm text-foreground">
          <input
            type="radio"
            name="cancel-timing"
            className="mt-1"
            checked={timing === 'period_end'}
            onChange={() => setTiming('period_end')}
          />
          <span>
            <span className="font-medium">At the end of the current period</span>
            {endDate && <span className="text-muted-foreground"> — your plan continues until {endDate}</span>}
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-foreground">
          <input
            type="radio"
            name="cancel-timing"
            className="mt-1"
            checked={timing === 'immediate'}
            onChange={() => setTiming('immediate')}
          />
          <span>
            <span className="font-medium">Immediately</span>
            {/* Not "access ends now": only Stripe's customer.subscription.deleted
                expires the tenant, so there is a short window after this where
                the customer is still working normally. The toast on confirm
                says the same thing. */}
            <span className="text-muted-foreground"> — you move to the free plan as soon as Stripe confirms</span>
          </span>
        </label>
      </div>

      <div className="mt-5 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
        <p className="mb-1 font-medium text-foreground">What happens</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Your subscription will not renew.</li>
          <li>No refund is issued for the current period.</li>
          <li>You move to the free plan: everything keeps working, but the AI features stop.</li>
          <li>Your data and settings are kept — they are not deleted.</li>
          <li>You can subscribe again at any time.</li>
        </ul>
      </div>

      <div className="mt-4">
        {/* Checkbox's onChange gives the event, unlike Input which gives the value. */}
        <Checkbox
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          label="I understand this cancels billing and stops the AI features."
        />
      </div>
    </BaseModal>
  )
}
